/**
 * Main App - Feature-flag driven architecture with error boundaries
 *
 * This is the primary and only App component. All development should focus on this file.
 * The feature-flag system allows safe incremental development and testing.
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { FeatureProvider, useFeature } from './contexts/FeatureContext';
import { SafeComponent, FeatureDisabledFallback } from './components/ui/SafeComponent';
import { FeatureTogglePanel } from './components/dev/FeatureTogglePanel';

// Import persistence hook
import { useAutoSave } from './stores/persistence';
import { useCharacterStore } from './stores/character';
import { getActiveCharacterId } from './utils/cookieUtils';
import { CombatRoundBar } from './components/combat/CombatRoundBar';

// Always-available components (no lazy loading for core UI)
import BasicTest from './pages/Testing/BasicTest';
import SimpleTest from './pages/Testing/SimpleTest';
import DebugSettings from './pages/Testing/DebugSettings';
import { RulesReference } from './pages/RulesReference';

// Lazy-loaded components with error boundaries
const Welcome = lazy(() => import('./pages/Welcome/Welcome'));
const CharacterSheet = lazy(() => import('./pages/CharacterSheet/CharacterSheet'));
const Combat = lazy(() => import('./pages/Combat/Combat'));
const Advancement = lazy(() => import('./pages/Advancement/Advancement'));
const HouseRules = lazy(() => import('./pages/HouseRules/HouseRules'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Shop = lazy(() => import('./pages/Shop'));
const GMPage = lazy(() => import('./pages/GM/GMPage'));
const SkillsTalents = lazy(() => import('./pages/SkillsTalents'));
// RulesReference now imported directly above
const RealGoogleDriveTest = lazy(() => import('./pages/Testing/RealGoogleDriveTest'));
const ShopTest = lazy(() => import('./pages/Testing/ShopTest'));
const RulebookSettingsTest = lazy(() => import('./pages/Testing/RulebookSettingsTest'));
const SkillsTabTest = lazy(() => import('./pages/Testing/SkillsTabTest'));
const ThemeTest = lazy(() => import('./pages/Testing/ThemeTest'));
// const GoogleAPIDebug = lazy(() => import('./pages/Testing/GoogleAPIDebug')) // Temporarily disabled due to type conflicts

// Safe wrapper for conditional features
function ConditionalFeature({
  feature,
  children,
  fallback,
  componentName
}: {
  feature: keyof import('./config/features').FeatureFlags;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  componentName: string;
}) {
  const isEnabled = useFeature(feature);

  if (!isEnabled) {
    return fallback || <FeatureDisabledFallback featureName={componentName} />;
  }

  return <SafeComponent componentName={componentName}>{children}</SafeComponent>;
}

// Main App Content
function AppContent() {
  // Initialize character persistence (auto-save only, no auto-load)
  console.log('🔄 App: Initializing persistence...');
  useAutoSave(); // Only enable auto-save, not auto-load
  console.log('✅ App: Persistence initialized');

  const compactMode = useSettingsStore((s) => s.compactMode);

  // Auto-load character from cookie on app mount
  const load = useCharacterStore((state) => state.load);
  const character = useCharacterStore((state) => state.character);

  React.useEffect(() => {
    const loadCharacterFromCookie = async () => {
      try {
        const activeCharacterId = getActiveCharacterId();
        if (activeCharacterId && !character) {
          console.log('🍪 App: Found character ID in cookie, loading...', activeCharacterId);
          const { localCharacterService } = await import('./services/LocalCharacterService');
          const loadedCharacter = await localCharacterService.loadCharacter(activeCharacterId);
          if (loadedCharacter) {
            console.log('✅ App: Character loaded from cookie');
            load(loadedCharacter);
          } else {
            console.log('📭 App: Character not found for cookie ID');
          }
        }
      } catch (error) {
        console.error('❌ App: Failed to load character from cookie:', error);
      }
    };

    loadCharacterFromCookie();
  }, [load, character]);

  return (
    <div className={compactMode ? 'p-1.5' : 'p-6'}>
      <CombatRoundBar />
      <Routes>
        <Route path="/" element={<Welcome />} />

        {/* Conditional Feature Routes */}
        <Route
          path="/character"
          element={
            <ConditionalFeature feature="CHARACTER_SHEET" componentName="Character Sheet">
              <CharacterSheet />
            </ConditionalFeature>
          }
        />

        <Route
          path="/combat"
          element={
            <ConditionalFeature feature="COMBAT_PAGE" componentName="Combat Page">
              <Combat />
            </ConditionalFeature>
          }
        />

        <Route
          path="/advancement"
          element={
            <ConditionalFeature feature="ADVANCEMENT_PAGE" componentName="Advancement Page">
              <Advancement />
            </ConditionalFeature>
          }
        />

        <Route path="/house-rules" element={<HouseRules />} />

        <Route
          path="/settings"
          element={
            <ConditionalFeature feature="SETTINGS_PAGE" componentName="Settings Page">
              <Settings />
            </ConditionalFeature>
          }
        />

        <Route
          path="/shop"
          element={
            <ConditionalFeature feature="SHOP_PAGE" componentName="Shop Page">
              <Shop />
            </ConditionalFeature>
          }
        />

        <Route
          path="/gm"
          element={
            <ConditionalFeature feature="GM_PAGE" componentName="GM Combat Tracker">
              <GMPage />
            </ConditionalFeature>
          }
        />

        {/* Always available routes */}
        <Route
          path="/rules"
          element={
            <SafeComponent componentName="Rules Reference">
              <RulesReference />
            </SafeComponent>
          }
        />
        <Route
          path="/skills"
          element={
            <SafeComponent componentName="Skills & Talents">
              <SkillsTalents />
            </SafeComponent>
          }
        />
        <Route
          path="/dice"
          element={
            <SafeComponent componentName="Dice Placeholder">
              <DicePlaceholder />
            </SafeComponent>
          }
        />

        {/* Test Routes */}
        <Route path="/test/basic" element={<BasicTest />} />
        <Route path="/test/simple" element={<SimpleTest />} />
        <Route path="/test/debug" element={<DebugSettings />} />
        <Route path="/test/theme" element={<ThemeTest />} />
        <Route path="/test/rulebook-settings" element={<RulebookSettingsTest />} />
        <Route path="/test/skills-tab" element={<SkillsTabTest />} />

        <Route path="/test/shop" element={<ShopTest />} />

        <Route
          path="/test/google-drive-real"
          element={
            <ConditionalFeature feature="GOOGLE_DRIVE_TESTS" componentName="Google Drive Test">
              <RealGoogleDriveTest />
            </ConditionalFeature>
          }
        />

        {/* Temporarily disabled due to type conflicts
            <Route 
              path="/test/google-api-debug" 
              element={
                <ConditionalFeature feature="GOOGLE_DRIVE_TESTS" componentName="Google API Debug">
                  <GoogleAPIDebug />
                </ConditionalFeature>
              } 
            />
            */}
      </Routes>
    </div>
  );
}

import { Sidebar } from './components/layout/Sidebar';

import { RightSidebar } from './components/layout/RightSidebar';
import { useSettingsStore } from './stores/settings';

function DicePlaceholder() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
        <h1 className="text-2xl font-bold text-ink mb-4">🎲 Dice Roller (Coming Soon)</h1>
        <p className="text-ink-dim">
          Standalone dice roller will be available when the Combat feature is enabled and stable.
        </p>
      </div>
    </div>
  );
}

// Main App with Feature Provider
function App() {
  // Get base path from Vite config (set via VITE_BASE_PATH env var or deployment.config.json)
  const basename = import.meta.env.BASE_URL || '/';

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const _compactMode = useSettingsStore((s) => s.compactMode);

  return (
    <FeatureProvider>
      <Router basename={basename}>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen bg-page text-ink">
              <div className="text-center">
                <div className="text-4xl mb-4">⚡</div>
                <div className="text-xl text-ink-dim">Loading...</div>
              </div>
            </div>
          }
        >
          <div className="flex h-screen bg-page text-ink overflow-hidden">
            <Sidebar
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            />

            <div className="flex-1 overflow-auto min-w-0">
              <AppContent />
            </div>

            <RightSidebar />

            {/* Feature Toggle Panel - only show if enabled */}
            <ConditionalFeature feature="FEATURE_TOGGLES" componentName="Feature Toggles">
              <FeatureTogglePanel />
            </ConditionalFeature>
          </div>
        </Suspense>
      </Router>
    </FeatureProvider>
  );
}

export default App;
